// 독립 연결 창을 처음 열 때 「어느 연결로 볼까」 를 묻는 작은 창 (2026-10-01).
//
// 독립 연결 창(오라클 성능 분석 · DBMS 카탈로그 · 자원 분석 · PL/SQL 편집기 · 세션 분석)은 창마다
// 전담 세션을 갖고, 창을 닫아도 그 세션을 남겨 다시 열 때 쓴다(hooks/useOwnConnSession). 그래서
// **그 창의 세션이 이미 있으면 묻지 않고** 바로 연다 — 이 창은 세션이 없을 때만 뜬다(Header 의
// openOwnConnWindow). 기본으로 고른 것은 편집기가 쓰는 연결이다.
//
// 창 안 조회들은 손대지 않는다 — 연결이 정해진 뒤에 창을 열기 때문이다. 창이 연 뒤에 연결을 고르게
// 하면 고르기 전까지 다섯 창의 조회가 연결 없이 나간다.
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AppModal from '../common/AppModal';
import { DbmsIcon } from '../../utils/dbmsIcon';
import './OwnConnChooser.css';

export default function OwnConnChooser({ open, title, icon, conns, defaultConnId, onConfirm, onCancel }) {
    const { t } = useTranslation();
    const [picked, setPicked] = useState(null);

    // 열릴 때마다 기본값으로 — 앞서 고른 것이 남아 있으면 다른 창의 선택을 들고 온다
    useEffect(() => {
        if (!open) return;
        const valid = (id) => !!id && conns.some(c => c.id === id);
        setPicked(valid(defaultConnId) ? defaultConnId : (conns[0]?.id || null));
    }, [open, conns, defaultConnId]);

    const confirm = () => { if (picked) onConfirm(picked); };

    return (
        <AppModal
            open={open}
            onClose={onCancel}
            title={title}
            icon={icon}
            className="monconn-modal"
        >
            <div className="monconn-body">
                <p className="monconn-lead">{t('monitoring:mc.lead')}</p>
                {conns.length === 0 ? (
                    <div className="monconn-empty">{t('monitoring:mt.noConnection')}</div>
                ) : (
                    <div className="monconn-list" role="radiogroup" aria-label={t('monitoring:mt.connection')}>
                        {conns.map(c => (
                            <label
                                key={c.id}
                                className={`monconn-item ${picked === c.id ? 'monconn-item-on' : ''}`}
                                onDoubleClick={() => onConfirm(c.id)}
                            >
                                <input
                                    type="radio"
                                    name="monconn"
                                    checked={picked === c.id}
                                    onChange={() => setPicked(c.id)}
                                />
                                <DbmsIcon dbType={c.dbType} size={14} />
                                <span className="monconn-name">{c.name}</span>
                                {c.id === defaultConnId && <span className="monconn-tag">{t('monitoring:mc.editorConn')}</span>}
                            </label>
                        ))}
                    </div>
                )}
                <p className="monconn-note">{t('monitoring:mc.keepNote')}</p>
            </div>
            <div className="monconn-footer">
                <button type="button" className="app-btn" onClick={onCancel}>{t('common:ui.cancel')}</button>
                <button type="button" className="app-btn app-btn-primary" onClick={confirm} disabled={!picked}>
                    {t('monitoring:mc.open')}
                </button>
            </div>
        </AppModal>
    );
}

OwnConnChooser.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.node.isRequired,
    icon: PropTypes.node,
    conns: PropTypes.array.isRequired,
    defaultConnId: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
